import { Component, Input, HostListener, Output, EventEmitter, OnDestroy } from '@angular/core';
import { Toast } from '../toast/toast.service';
import { ImageService } from '../../services/image/image.service';
import { Subscription } from 'rxjs/Subscription';
import { ImageUploadService } from 'app/shared/services/image-upload/image-upload.service';

@Component({
  selector: 'tog-draggable-image-area',
  templateUrl: './draggable-image-area.component.html',
  styleUrls: ['./draggable-image-area.component.scss']
})
export class DraggableImageAreaComponent implements OnDestroy {
  @Input() app: string = '';
  @Input() imageName: string = '';
  @Input() uploadFile: boolean = true;
  @Input() cropperName: string = '';
  @Input() maxFileSize: number = 512;
  @Input() canDownload: boolean = false;

  displaySrc: string = '';
  dropStateActive: boolean = false;
  allowDragLeave: boolean = false;

  @Output() imageChanged: EventEmitter<File> = new EventEmitter<File>();
  @Output() editImage = new EventEmitter();
  @Output() invalidFileType = new EventEmitter();
  @Output() maxFileSizeExceeded = new EventEmitter();

  imageServiceSubscription: Subscription;

  constructor(public toast: Toast, public imageService: ImageService, public imageUploadService: ImageUploadService) {
    this.imageServiceSubscription = this.imageService.imageCropped.subscribe(e => {
      let self = this;
      if (e && e.name && e.name === this.cropperName) this.imageToDisplay = e.image;
    });
  }

  imageSelected(e: any) {
    if (e.target && e.target.files) {
      this.readFileForImage(e.target.files[0]);
    }
  }

  readFileForImage(file: File) {
    if (!file) return;
    let self = this;
    let fileReader = new FileReader();
    fileReader.readAsDataURL(file);

    // When the file has loaded, check to see if it's valid
    fileReader.onloadend = function (e: any) {

      // Attempt to load the image
      let image = new Image();
      image.src = e.target.result;

      image.onerror = () => {
        self.toast.error('Invalid file type. Allowed types: .png, .gif, .jpg, .jpeg');
        self.invalidFileType.emit();
      };

      image.onload = () => {
        if (self.maxFileSize > 0 && (e.loaded / 1000) > self.maxFileSize) {
          self.toast.error('Maximum file size exceeded. ' + self.maxFileSize + 'KB allowed.');
          self.maxFileSizeExceeded.emit();
        } else {
          if (self.uploadFile && self.app) {
            self.imageUploadService.uploadOriginalImage(self.app, file)
            .then(() => {
              self.broadcastImage(image, file);
            });
          } else self.broadcastImage(image, file);
        }
      };
    };
  }

  broadcastImage(image: HTMLImageElement, imageFile: File) {
    if (this.cropperName) this.imageService.setImageToBeCropped(image, this.cropperName);
    this.imageChanged.emit(imageFile);
    this.editImage.emit();
  }

  @Input() set imageToDisplay(imageURL: string) {
    if (imageURL) {
      this.displaySrc = imageURL;
      // If it's a URL, convert it to data
      if (imageURL.indexOf('http') === 0 && this.cropperName) {
        this.imageService.setImageURLToBeCropped(imageURL, this.cropperName);
      }
    }
  }

  reset() {
    this.displaySrc = '';
  }

  // Handlers for file drag and drop
  @HostListener('dragover', ['$event'])
  public onDragOver(e: any): void {
    this.dropStateActive = true;
    this.allowDragLeave = false;
    this.preventAndStopEventPropagation(e);
  }

  @HostListener('dragleave', ['$event'])
  public onDragLeave(e: Event): void {
    this.allowDragLeave = true;
    setTimeout(() => {
      if (this.allowDragLeave) this.dropStateActive = false;
      this.preventAndStopEventPropagation(e);
    }, 100);
  }

  @HostListener('drop', ['$event'])
  public onDrop(e: any) {
    this.dropStateActive = false;
    let files = e.target.files || e.dataTransfer.files;
    if (files.length) this.readFileForImage(files[0]);
    this.preventAndStopEventPropagation(e);
  }

  preventAndStopEventPropagation(e: any) {
    e.preventDefault();
    e.stopPropagation();
  }

  ngOnDestroy() {
    if (this.imageServiceSubscription) this.imageServiceSubscription.unsubscribe();
  }
}



// WEBPACK FOOTER //
// /app/src/app/shared/components/draggable-image-area/draggable-image-area.component.ts